import { Button, Field, Input } from '@aurea/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthCard } from '../components/AuthCard';
import { api } from '../lib/api';
import { useErrorText } from '../lib/i18n';

export const Route = createFileRoute('/login')({
  validateSearch: z.object({ next: z.string().optional() }),
  component: Login,
});

function Login() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { next } = Route.useSearch();
  const [remember, setRemember] = useState(true);
  const login = useMutation({
    mutationFn: api.login,
    onSuccess: async () => {
      qc.clear();
      await navigate({ to: next && next.startsWith('/') && !next.startsWith('//') ? next : '/' });
    },
  });

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    login.mutate({ email: String(f.get('email')), password: String(f.get('password')), remember });
  };

  return (
    <AuthCard title={t('login.title')}>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <Field label={t('login.email')}>
          <Input name="email" type="email" autoComplete="username" required autoFocus />
        </Field>
        <Field label={t('login.password')} error={login.isError ? errorText(login.error) : undefined}>
          <Input
            name="password"
            type="password"
            autoComplete="current-password"
            required
            invalid={login.isError}
          />
        </Field>
        <label className="check">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          {t('login.remember')}
        </label>
        <Button type="submit" loading={login.isPending} className="mt-2 w-full">
          {t('login.submit')}
        </Button>
        <Link to="/forgot" className="btn-link self-start">
          {t('login.forgot')}
        </Link>
      </form>
    </AuthCard>
  );
}
